import { AppData, VideoPlayer } from '@lightningjs/sdk';
import Lightning from '@lightningjs/sdk/src/Lightning';
import DashPlayerEngine from 'components/pages/playback/player-engines/DashPlayerEngine';
import HlsPlayerEngine from 'components/pages/playback/player-engines/HlsPlayerEngine';
import { buildParamsStringFromObj, getAdTagParams } from 'support/playerUtils';
import AbstractPlayer from 'components/pages/playback/player-components/AbstractPlayer';
import {
  Ad,
  CustomPlayerEvent,
  ImaEvent,
  ImaStreamManager,
} from 'types/player';
import ImaAdManager from 'components/pages/playback/ad-managers/ImaAdManager';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';

interface LightningPlayerTemplateSpec extends Lightning.Component.TemplateSpec {
  AdManager: typeof ImaAdManager;
}

const RETRY_AD_START_LIMIT = 1;

type ListenerCallback = (event: any) => void;

export default class LightningPlayer extends AbstractPlayer<LightningPlayerTemplateSpec> {
  readonly playerType = 'lightning';

  // Stream Manager: https://developers.google.com/ad-manager/dynamic-ad-insertion/sdk/html5/reference/js/StreamManager
  protected streamManager: ImaStreamManager | null = null;

  // Manager event listener properties
  private listeners: Array<[string, ListenerCallback]> = [];
  private listenersAttached = false;

  private googleImaApi: any | undefined;

  protected override _AdManager = this.getByRef('AdManager')!;

  protected override _engine: HlsPlayerEngine | DashPlayerEngine | undefined;

  // Live stream asset key.
  private contentSourceId = AppData?.google.contentSourceId;

  private imaStreamUrl = '';
  private imaTextTrack: { language: string; webvtt: string }[] = [];
  private reloadResumeStreamTime?: number = undefined;

  private retryAdStart = 0;

  static override _template(): Lightning.Component.Template<LightningPlayerTemplateSpec> {
    return {
      ...super._template(),
      AdManager: {
        type: ImaAdManager,
        passSignals: {
          cuePointsChanged: true,
          interactiveAdInit: true,
        },
      },
    };
  }

  override initialize(consumer: Lightning.Component): void {
    super.initialize(consumer);

    this.contentSourceId = AppData?.google.contentSourceId;

    VideoPlayer.consumer(consumer);
    VideoPlayer.loader(this.loader.bind(this));
    VideoPlayer.unloader(this.unloader.bind(this));
  }

  override _inactive() {
    this.retryAdStart = 0;
  }

  private async _load(
    videoEl: HTMLMediaElement,
    loadStream: () => Promise<void>,
  ) {
    const hasElement = document.getElementsByTagName('video').length > 0;
    if (!hasElement) {
      document.body.appendChild(videoEl);
    }

    this.videoElement = videoEl;
    this.videoElement.autoplay = true;
    this.videoElement.controls = false;
    this.videoElement.crossOrigin = 'anonymous';

    this.updateVideoElemDimensions({
      x: this._absoluteX,
      y: this._absoluteY,
      w: this.w,
      h: this.h,
    });

    this._engine =
      this.hasDrm && this.drmLicenseUrl
        ? new DashPlayerEngine(
            this.videoElement,
            this.drmLicenseUrl,
            this.hasDrm && !!this.liveStreamUrl,
          )
        : new HlsPlayerEngine(this.videoElement, !!this.liveStreamUrl);
    this._engine.ccSuppressed = this._ccSuppressed;

    if (this._onStreamAdaption) {
      this._engine.onAdaption(this._onStreamAdaption);
    }

    if (this._onPlaybackError) {
      this._engine.onError(this._onPlaybackError);
    }

    await loadStream();

    AppData?.announcerService.setOnSpeakEvent(
      this.onSpeakEventHandler.bind(this),
    );
  }

  private async loader(
    streamUrl: string,
    videoEl: HTMLMediaElement,
  ): Promise<void> {
    await this._load(videoEl, async () => {
      if (streamUrl) {
        await this._requestLiveStream(streamUrl, videoEl);
      } else {
        await this._requestVODStream(videoEl);
      }
    });
  }

  protected async unloader(videoEl: HTMLMediaElement) {
    this._unload(videoEl, true);
  }

  private _unload(videoEl: HTMLMediaElement, cleanupManager = true) {
    this.firePlayerEvent(CustomPlayerEvent.UNLOADING);

    this._engine?.destroy();
    this._engine = undefined;

    if (cleanupManager) {
      this.destroyManager();
    }

    videoEl.removeAttribute('src');
    videoEl.remove(); // remove the element from the document, element reference is still stored in VideoPlayer state
    AppData?.announcerService.removeOnSpeakEvent();
    videoEl.load();
  }

  // Temporary unload / "stop"
  public override unload() {
    if (this.liveStreamUrl) return; // Add support for live when needed

    this.unloadVod();
  }

  private unloadVod() {
    VideoPlayer.pause();

    // Unloads DAI listeners because player unloading seems to indirectly cause the manager
    // to think its at time = 0, and send unwanted ad events (start, progress, cue, etc).
    this.removeEventListenerFromManager(false);
    this._unload(this.videoElement as HTMLMediaElement, false);
  }

  public override reload(startTime: number) {
    if (this.liveStreamUrl) return; // Add support for live when needed

    this.reloadVod(startTime);
  }

  private reloadVod(startTime: number) {
    this.addEventListenerToManager();

    this.reloadResumeStreamTime = startTime;
    VideoPlayer.loader(this.reloader.bind(this));
    // Necessary to properly restore player events that were deregistered during unload
    this.open();
  }

  private async reloader(
    streamUrl: string,
    videoEl: HTMLMediaElement,
  ): Promise<void> {
    await this._load(videoEl, async () => {
      this._loadUrl(this.imaStreamUrl, this.reloadResumeStreamTime);
      this.reloadResumeStreamTime = undefined;
    });

    this._engine?.extractExternalTextTracks(this.imaTextTrack);

    // Restore the original loader
    VideoPlayer.loader(this.loader.bind(this));
  }

  protected updateVideoElemDimensions(dimensions: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
  }) {
    const { x, y, w, h } = dimensions;

    if (x !== undefined) {
      VideoPlayer.position(y ?? this._absoluteY, x);
    }

    if (y !== undefined) {
      VideoPlayer.position(y, x ?? this._absoluteX);
    }

    if (!!w) {
      VideoPlayer.size(w, h ?? this.h);
    }

    if (!!h) {
      VideoPlayer.size(w ?? this.w, h);
    }
  }

  // Stream events: https://developers.google.com/interactive-media-ads/docs/sdks/html5/dai/reference/js/StreamEvent
  protected _onStreamEvent(e: any) {
    if (!this.streamManager) {
      this.handleError('Stream manager not initialized');
      return;
    }

    switch (e.type) {
      case ImaEvent.LOADED:
        // Hold onto these if we need to stop and reload the player
        this.imaTextTrack = e.getStreamData().subtitles;
        this.imaStreamUrl = e.getStreamData().url;

        this._engine?.extractExternalTextTracks(this.imaTextTrack);
        this._loadUrl(
          this.imaStreamUrl,
          this.startTime
            ? this.streamManager.streamTimeForContentTime(this.startTime)
            : undefined,
        );
        break;
      case ImaEvent.ERROR:
        this._onStreamError?.(true, e.getStreamData().errorMessage);
        break;
      case ImaEvent.AD_BREAK_ENDED:
        VideoPlayer.play();
        this.retryAdStart = 0;
        this.firePlayerEvent(e.type, e);
        break;
      default:
        this.firePlayerEvent(e.type, e);
        break;
    }
  }

  private async _loadUrl(url: string, startTime?: number) {
    if (!this.streamManager) {
      this.handleError('StreamManager missing in streamRequest');
      return;
    }

    this._onStreamLoaded?.(url);
    await this._engine?.load(startTime, url);
  }

  protected async _requestLiveStream(
    streamUrl: string,
    videoEl: HTMLMediaElement,
  ) {
    let urlWithParams: string;
    try {
      const liveStreamType = this.liveStreamType;
      const adZone = this.adZone;
      let streamUrlWithParams = streamUrl ?? '';

      // An empty or 'uplynk' livestream type indicates we are playing live content on
      // the live playback page / EPG page
      if (liveStreamType && liveStreamType !== 'uplynk') {
        const paramsString = buildParamsStringFromObj(
          AppData!.adParams!.platform.fast_params,
        );
        // TODO: Potentially update amagi channel stream cohorts
        streamUrlWithParams = `${streamUrl}?${paramsString}`;
      }

      const adTagParams = await getAdTagParams(
        { url: streamUrlWithParams },
        '',
        {
          adZone: adZone,
          isLive: true,
          shouldEncodeParams: true,
        },
      );

      urlWithParams = adTagParams.url ?? streamUrlWithParams;
    } catch (e) {
      this.handleError(e as string | Error);
      return;
    }

    this._beforeLoad?.(videoEl as HTMLVideoElement);
    await this._engine?.load(this.startTime, urlWithParams);
    this._onStreamLoaded?.(urlWithParams);
  }

  protected createManager(videoEl: HTMLVideoElement) {
    this.destroyManager();

    try {
      this.googleImaApi = window.google.ima.dai.api;

      this.streamManager = new this.googleImaApi.StreamManager(videoEl);
    } catch (e) {
      return this.handleError(e as Error | string);
    }

    this._AdManager.initialize();
    this._AdManager.setStreamManager(this.streamManager!);
    this._beforeLoad?.(videoEl as HTMLVideoElement);

    // Stream Event Types: https://developers.google.com/ad-manager/dynamic-ad-insertion/sdk/html5/reference/js/StreamEvent

    const listenerTypes = [
      this.googleImaApi.StreamEvent.Type.LOADED,
      this.googleImaApi.StreamEvent.Type.AD_BREAK_STARTED,
      this.googleImaApi.StreamEvent.Type.AD_BREAK_ENDED,
      this.googleImaApi.StreamEvent.Type.ERROR,
      this.googleImaApi.StreamEvent.Type.CUEPOINTS_CHANGED,
      this.googleImaApi.StreamEvent.Type.AD_PROGRESS,

      // Required by Conviva
      this.googleImaApi.StreamEvent.Type.STARTED,
      this.googleImaApi.StreamEvent.Type.FIRST_QUARTILE,
      this.googleImaApi.StreamEvent.Type.MIDPOINT,
      this.googleImaApi.StreamEvent.Type.THIRD_QUARTILE,
      this.googleImaApi.StreamEvent.Type.COMPLETE,
      this.googleImaApi.StreamEvent.Type.SKIPPED,
    ];
    const callback = (event: any) => {
      this._onStreamEvent(event);
      this._AdManager.onStreamEvent(event);
    };

    listenerTypes.forEach((type: string) => {
      this.listeners.push([type, callback]);
    });

    this.addEventListenerToManager();
  }

  private addEventListenerToManager() {
    if (this.listenersAttached || !this.streamManager) return;

    this.listeners.forEach(([type, callback]) => {
      this.streamManager?.addEventListener(type, callback);
    });
    this.listenersAttached = true;
  }

  private removeEventListenerFromManager(cleanup = true) {
    if (this.listenersAttached) {
      this.listeners.forEach(([type, callback]) => {
        this.streamManager?.removeEventListener(type, callback);
      });
      this.listenersAttached = false;
    }

    if (cleanup) this.listeners = [];
  }

  protected destroyManager() {
    this.removeEventListenerFromManager();

    this.streamManager?.reset();
    this.streamManager = null;
  }

  protected async _requestVODStream(videoEl: HTMLMediaElement) {
    this.createManager(videoEl as HTMLVideoElement);

    if (!this.contentSourceId || !this.videoId) {
      this.handleError('invalid/missing content for player');
      return;
    }

    if (!this.streamManager) {
      this.handleError('StreamManager missing in streamRequest');
      return;
    }

    let adTagParams: Record<string, string> = {};
    try {
      adTagParams = await getAdTagParams(
        AppData?.adParams?.platform.vod_params,
        this.videoId,
        { w: this.w, h: this.h },
      );
    } catch (e) {
      this.handleError(e as Error | string);
      return;
    }

    const { apiKey } = AppData!.google;

    const streamRequest = new this.googleImaApi.VODStreamRequest();
    streamRequest.contentSourceId = this.contentSourceId;
    streamRequest.videoId = this.videoId;
    streamRequest.apiKey = apiKey;
    streamRequest.format = this.hasDrm ? 'dash' : 'hls';
    streamRequest.enableNonce = true;
    streamRequest.adTagParameters = adTagParams;

    this.streamManager?.requestStream(streamRequest);
  }

  protected rawSeek(seekTime: number) {
    VideoPlayer.seek(seekTime);
  }

  // called before initialize
  override open() {
    super.open();
    if (this.liveStreamUrl) {
      VideoPlayer.open(this.liveStreamUrl);
    } else {
      // URL is needed to trigger videoPlayer.open() that triggers the loader
      VideoPlayer.open('');
    }
  }

  override close() {
    super.close();
    this._preSeekContentTime = null;
    this._snapForwardRawTime = null;
    this.imaStreamUrl = '';
    this.imaTextTrack = [];
    this.reloadResumeStreamTime = undefined;

    VideoPlayer.close();
  }

  isPlaying(): boolean {
    return VideoPlayer.playing;
  }

  resume() {
    if (!VideoPlayer.playing) {
      VideoPlayer.playPause();
    }
  }

  pause() {
    VideoPlayer.pause();
  }

  currentContentTime(): number {
    return (
      this.streamManager?.contentTimeForStreamTime(VideoPlayer.currentTime) ??
      VideoPlayer.currentTime
    );
  }

  currentRawTime(): number {
    return VideoPlayer.currentTime;
  }

  streamTimeForContentTime(time: number): number | undefined {
    return this.streamManager?.streamTimeForContentTime(time);
  }

  contentTimeForStreamTime(time: number): number | undefined {
    return this.streamManager?.contentTimeForStreamTime(time);
  }

  previousCuePointForStreamTime(time: number): AdCuePoint | undefined {
    return this.streamManager?.previousCuePointForStreamTime(time) ?? undefined;
  }

  override extractAd(event: any): Ad | undefined {
    return event?.getAd();
  }

  override verifyCuepointPlaying(rawTime: number) {
    // Only do this check if we are seeking to an ad for timeupdate optimization
    if (!this._snapForwardRawTime) return;

    const cuepoint = this._AdManager.getNextCuePointFrom(rawTime);
    if (!cuepoint) return;

    // Exclude boundaries because ima not yet updated the cuepoint
    const adRetry =
      !cuepoint?.played && cuepoint.start < rawTime && rawTime < cuepoint.end;

    if (adRetry && this.retryAdStart < RETRY_AD_START_LIMIT) {
      this.retryAdStart++;
      this.rawSeek(cuepoint.start);
      VideoPlayer.play();
    }
  }
}
